import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ComponentLayoutThumbnail.module.css';
import { contentfulEntryUrl } from './contentful';
import { getSectionColor, THUMBNAIL_SECTION_ORDER } from './sectionColors';
import { useDraggablePosition } from './useDraggablePosition';

export interface ExpectedComponent {
  id: string;
  name: string;
  section: string;
}

interface TypeStat {
  expected: number;
  ids: string[];
  name: string;
  rendered: number;
  renderedIds: Set<string>;
}

interface SectionStat {
  missing: number;
  section: string;
  types: TypeStat[];
}

interface ComponentLayoutThumbnailProps {
  expectedLayout: ExpectedComponent[];
  templateId?: string;
  templateName?: string;
}

interface RenderedMeasurement {
  counts: Map<string, number>;
  idsByKey: Map<string, Set<string>>;
}

// Count actually-rendered components in the live DOM, keyed by `${section}|${name}`.
// renderComponents() tags every successfully-rendered component with these data attributes,
// so a component present in the template but absent here failed to render (timeout, error,
// invalid dehydrated state, etc). Also collects the specific Contentful entry ids that DID
// render, so each pill can be coloured by its own status rather than only by the aggregate count.
function measureRenderedComponents(): RenderedMeasurement {
  const counts = new Map<string, number>();
  const idsByKey = new Map<string, Set<string>>();
  document.querySelectorAll<HTMLElement>('[data-overlay-name]').forEach(el => {
    const name = el.dataset.overlayName ?? '';
    const section = el.dataset.overlaySection ?? '';
    const id = el.dataset.overlayId ?? '';
    const key = `${section}|${name}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    if (id) {
      const set = idsByKey.get(key) ?? new Set<string>();
      set.add(id);
      idsByKey.set(key, set);
    }
  });
  return { counts, idsByKey };
}

function buildSectionStats(expectedLayout: ExpectedComponent[], rendered: RenderedMeasurement): SectionStat[] {
  // expected[section][name] = list of Contentful entry ids (occurrence-preserving)
  const expectedBySection = new Map<string, Map<string, string[]>>();
  for (const { id, name, section } of expectedLayout) {
    const byName = expectedBySection.get(section) ?? new Map<string, string[]>();
    const ids = byName.get(name) ?? [];
    ids.push(id);
    byName.set(name, ids);
    expectedBySection.set(section, byName);
  }

  // Stable ordering: known sections first, then any unexpected ones.
  const orderedSections = [
    ...THUMBNAIL_SECTION_ORDER.filter(s => expectedBySection.has(s)),
    ...[...expectedBySection.keys()].filter(s => !THUMBNAIL_SECTION_ORDER.includes(s as never)),
  ];

  return orderedSections.map(section => {
    const byName = expectedBySection.get(section)!;
    const types: TypeStat[] = [...byName.entries()]
      .map(([name, ids]) => {
        const key = `${section}|${name}`;
        const renderedIds = rendered.idsByKey.get(key) ?? new Set<string>();
        return {
          name,
          ids,
          expected: ids.length,
          rendered: Math.min(rendered.counts.get(key) ?? 0, ids.length),
          renderedIds,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const missing = types.reduce((sum, t) => sum + (t.expected - t.rendered), 0);
    return { section, types, missing };
  });
}

export function ComponentLayoutThumbnail({ expectedLayout, templateId, templateName }: ComponentLayoutThumbnailProps) {
  const [mounted, setMounted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sectionStats, setSectionStats] = useState<SectionStat[]>([]);
  const rafRef = useRef<number | null>(null);
  const { containerRef, dragHandlers, style, wasDragged } = useDraggablePosition('componentLayoutThumbnail:pos');

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setSectionStats(buildSectionStats(expectedLayout, measureRenderedComponents()));
    });
  }, [expectedLayout]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    scheduleUpdate();

    // Debounced MutationObserver to catch async/lazy hydration that fills in (or never fills in)
    // late-rendering components.
    let mutationDebounce: ReturnType<typeof setTimeout> | null = null;
    const debouncedScheduleUpdate = () => {
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      mutationDebounce = setTimeout(scheduleUpdate, 150);
    };

    // Each page template tags its own root with `data-overlay-root` (Dynamic/Landing/SportsHub/
    // SportsGrid in RR7, DynamicPageTemplate.astro in atlas-astro). Fall back to <body> so a new
    // template that forgets the attribute still re-measures, just against a noisier root.
    const mutationObserver = new MutationObserver(debouncedScheduleUpdate);
    const wrapper = document.querySelector<HTMLElement>('[data-overlay-root]') ?? document.body;
    mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: false });

    return () => {
      mutationObserver.disconnect();
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [mounted, scheduleUpdate]);

  if (!mounted || expectedLayout.length === 0) return null;

  const totalExpected = sectionStats.reduce((sum, s) => sum + s.types.reduce((n, t) => n + t.expected, 0), 0);
  const totalMissing = sectionStats.reduce((sum, s) => sum + s.missing, 0);

  return (
    <div className={styles.thumbnail} ref={containerRef} style={style}>
      <button
        className={styles.header}
        onClick={() => {
          if (wasDragged()) return;
          setCollapsed(c => !c);
        }}
        title="Drag to move. Click to collapse. Expected page layout from the data-templates API. Components shown here but missing from the page failed to render."
        type="button"
        {...dragHandlers}
      >
        <span className={styles.title}>Template layout</span>
        <span className={totalMissing > 0 ? styles.statBad : styles.statOk}>
          {totalMissing > 0 ? `${totalMissing} missing` : 'all rendered'}
        </span>
        <span className={styles.statMuted}>{totalExpected} expected</span>
        <span className={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
        <div className={styles.body}>
          {templateName &&
            (templateId ? (
              <a
                className={styles.templateRow}
                href={contentfulEntryUrl(templateId)}
                rel="noopener noreferrer"
                target="_blank"
                title={`Open Contentful entry ${templateId}`}
              >
                <span className={styles.templateRowLabel}>Template</span>
                <span className={styles.templateRowValue}>{templateName}</span>
              </a>
            ) : (
              <div className={styles.templateRow}>
                <span className={styles.templateRowLabel}>Template</span>
                <span className={styles.templateRowValue}>{templateName}</span>
              </div>
            ))}
          {sectionStats.map(({ missing, section, types }) => {
            const color = getSectionColor(section);
            return (
              <div className={styles.section} key={section}>
                <div className={styles.sectionHeader} style={{ backgroundColor: color }}>
                  <span className={styles.sectionLabel}>{section}</span>
                  {missing > 0 && <span className={styles.sectionMissing}>{missing} missing</span>}
                </div>
                <div className={styles.chips}>
                  {types.map(({ expected, ids, name, rendered, renderedIds }) => {
                    const isMissing = rendered < expected;
                    const chipClass = isMissing ? styles.chipMissing : styles.chip;
                    const chipTitle = isMissing
                      ? `${name}: ${rendered}/${expected} rendered — ${expected - rendered} failed to render`
                      : `${name}: ${rendered}/${expected} rendered`;
                    const chipBody = (
                      <>
                        <span className={styles.chipName}>{name}</span>
                        <span className={styles.chipCount}>
                          {rendered}/{expected}
                        </span>
                      </>
                    );

                    // `ids` may carry falsy entries (atlas-astro's ResolvedComponent.id is optional),
                    // so filter once and drive the link shape off the linkable count. Expected/rendered
                    // counts come from `ids.length` and stay unchanged — a missing id means "we can't
                    // deep-link this instance", not "this instance doesn't count".
                    const linkableIds = ids.filter((id): id is string => Boolean(id));

                    if (linkableIds.length === 0) {
                      return (
                        <div className={chipClass} key={name} title={chipTitle}>
                          {chipBody}
                        </div>
                      );
                    }

                    // Single linkable id → whole chip is the link to that Contentful entry.
                    if (linkableIds.length === 1) {
                      return (
                        <a
                          className={chipClass}
                          href={contentfulEntryUrl(linkableIds[0])}
                          key={name}
                          rel="noopener noreferrer"
                          target="_blank"
                          title={`${chipTitle} — open Contentful entry ${linkableIds[0]}`}
                        >
                          {chipBody}
                        </a>
                      );
                    }

                    // Multiple linkable ids → chip is not a link; render small #1 #2 … links after it.
                    // Colour each pill by its own status so the missing instances jump out — 8/12
                    // rendered is only useful if you can also see WHICH 4 failed.
                    return (
                      <div className={styles.chipRow} key={name}>
                        <div className={chipClass} title={chipTitle}>
                          {chipBody}
                        </div>
                        <div className={styles.chipIds}>
                          {linkableIds.map((componentId, i) => {
                            const didRender = renderedIds.has(componentId);
                            return (
                              <a
                                className={didRender ? styles.chipIdLink : styles.chipIdLinkMissing}
                                href={contentfulEntryUrl(componentId)}
                                key={`${componentId}-${i}`}
                                rel="noopener noreferrer"
                                target="_blank"
                                title={
                                  didRender
                                    ? `Rendered — open Contentful entry ${componentId}`
                                    : `Failed to render — open Contentful entry ${componentId}`
                                }
                              >
                                #{i + 1}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
